import { Link, useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  ExternalLink,
  Zap,
  Trash2,
  RefreshCw,
  BarChart3,
  Users,
  TrendingUp,
  Loader2,
  CheckCircle,
  AlertCircle,
  Cloud,
  Server,
  Link2,
} from 'lucide-react'
import { api } from '@/lib/api'
import {
  formatDate,
  formatNumber,
  getStatusColor,
  getStrategyLabel,
  getStrategyDescription,
  cn,
} from '@/lib/utils'
import toast from 'react-hot-toast'

export function DomainDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['domain', id],
    queryFn: () => api.getDomain(id!),
    enabled: !!id,
  })

  const { data: operationsData, isLoading: operationsLoading } = useQuery({
    queryKey: ['domain-operations', id],
    queryFn: () => api.getDomainOperations(id!),
    enabled: !!id,
  })

  const verifyMutation = useMutation({
    mutationFn: () => api.verifyDomain(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domain', id] })
      toast.success('Domain verified!')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const analyzeMutation = useMutation({
    mutationFn: () => api.analyzeDomain(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domain', id] })
      toast.success('Analysis complete!')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteDomain(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domains'] })
      toast.success('Domain deleted')
      navigate('/dashboard/domains')
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (error || !data?.domain) {
    return (
      <div className="text-center py-20">
        <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
        <h2 className="text-xl font-semibold mb-2">Domain not found</h2>
        <p className="text-muted-foreground mb-4">
          The domain you're looking for doesn't exist or you don't have access to it.
        </p>
        <Link to="/dashboard/domains" className="btn-primary">
          Back to Domains
        </Link>
      </div>
    )
  }

  const domain = data.domain
  const analysis = domain.aiAnalysis
  const operations = operationsData || null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/dashboard/domains" className="btn-ghost p-2">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-display font-bold">{domain.domain_name}</h1>
            <a
              href={`https://${domain.domain_name}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground"
            >
              <ExternalLink className="h-5 w-5" />
            </a>
          </div>
          <p className="text-muted-foreground">
            Added {formatDate(domain.created_at)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Always show Generate button - verification not required for testing */}
          <Link
            to={`/dashboard/generate/${domain.id}`}
            className="btn-primary"
          >
            <Zap className="h-4 w-4" />
            Generate Site
          </Link>
          {domain.verification_status !== 'verified' && (
            <button
              onClick={() => verifyMutation.mutate()}
              disabled={verifyMutation.isPending}
              className="btn-outline"
            >
              {verifyMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Verify
                </>
              )}
            </button>
          )}
          <button
            onClick={() => {
              if (confirm('Are you sure you want to delete this domain?')) {
                deleteMutation.mutate()
              }
            }}
            className="btn-outline text-destructive hover:bg-destructive/10"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <p className="text-sm text-muted-foreground mb-1">Status</p>
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'badge',
                `badge-${getStatusColor(domain.verification_status)}`
              )}
            >
              {domain.verification_status === 'verified' && (
                <CheckCircle className="h-3 w-3 mr-1" />
              )}
              {domain.verification_status}
            </span>
          </div>
        </div>

        <div className="card">
          <p className="text-sm text-muted-foreground mb-1">Strategy</p>
          <p className="font-semibold">
            {domain.monetization_strategy
              ? getStrategyLabel(domain.monetization_strategy)
              : 'Not selected'}
          </p>
        </div>

        <div className="card">
          <p className="text-sm text-muted-foreground mb-1">Total Visits</p>
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            <p className="text-2xl font-display font-bold">
              {formatNumber(domain.total_visits)}
            </p>
          </div>
        </div>

        <div className="card">
          <p className="text-sm text-muted-foreground mb-1">Total Leads</p>
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-success" />
            <p className="text-2xl font-display font-bold">
              {formatNumber(domain.total_leads)}
            </p>
          </div>
        </div>
      </div>

      {/* AI Analysis */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">AI Analysis</h2>
          {/* Always allow analysis - no verification required */}
          <button
            onClick={() => analyzeMutation.mutate()}
            disabled={analyzeMutation.isPending}
            className="btn-outline btn-sm"
          >
            {analyzeMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                {analysis ? 'Re-analyze' : 'Analyze'}
              </>
            )}
          </button>
        </div>

        {!analysis ? (
          <div className="text-center py-8">
            <TrendingUp className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground mb-3">
              Run AI analysis to get monetization recommendations
            </p>
            <button
              onClick={() => analyzeMutation.mutate()}
              disabled={analyzeMutation.isPending}
              className="btn-primary"
            >
              {analyzeMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4" />
                  Analyze Domain
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Overview */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground mb-1">Niche</p>
                <p className="font-semibold">{analysis.niche}</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground mb-1">Target Audience</p>
                <p className="font-semibold">{analysis.targetAudience}</p>
              </div>
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground mb-1">Commercial Intent</p>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full"
                      style={{ width: `${analysis.commercialIntent * 10}%` }}
                    />
                  </div>
                  <span className="font-semibold">{analysis.commercialIntent}/10</span>
                </div>
              </div>
              <div className="p-4 rounded-lg bg-muted/50">
                <p className="text-sm text-muted-foreground mb-1">Keywords</p>
                <p className="font-semibold">{analysis.keywords.slice(0, 3).join(', ')}</p>
              </div>
            </div>

            {/* Suggested Strategies */}
            <div>
              <h3 className="font-semibold mb-3">Recommended Strategies</h3>
              <div className="space-y-3">
                {analysis.suggestedStrategies.map((strategy, i) => (
                  <div
                    key={strategy.strategy}
                    className={cn(
                      'p-4 rounded-lg border',
                      i === 0 ? 'border-primary bg-primary/5' : 'border-border'
                    )}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">
                            {getStrategyLabel(strategy.strategy)}
                          </p>
                          {i === 0 && (
                            <span className="badge badge-primary text-xs">
                              Best Match
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {getStrategyDescription(strategy.strategy)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-display font-bold text-primary">
                          {strategy.fitScore}%
                        </p>
                        <p className="text-xs text-muted-foreground">fit score</p>
                      </div>
                    </div>
                    <p className="text-sm">{strategy.reasoning}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Content Ideas */}
            <div>
              <h3 className="font-semibold mb-3">Content Ideas</h3>
              <ul className="space-y-2">
                {analysis.contentIdeas.map((idea, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span className="text-sm">{idea}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* SEO Keywords */}
            <div>
              <h3 className="font-semibold mb-3">SEO Keywords</h3>
              <div className="flex flex-wrap gap-2">
                {analysis.seoKeywords.map((keyword) => (
                  <span key={keyword} className="badge badge-muted">
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Domain Operations */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Domain Operations</h2>
          <button
            onClick={() =>
              queryClient.invalidateQueries({ queryKey: ['domain-operations', id] })
            }
            className="btn-outline btn-sm"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>

        {operationsLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : !operations ? (
          <p className="text-sm text-muted-foreground">
            Operations data unavailable.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 mb-2">
                  <Cloud className="h-4 w-4 text-primary" />
                  <p className="text-sm font-semibold">Cloudflare</p>
                </div>
                <p className="text-sm text-muted-foreground">
                  {operations.connected ? 'Connected' : 'Not connected'}
                </p>
                {operations.cloudflareError && (
                  <p className="text-xs text-destructive mt-2">
                    {operations.cloudflareError}
                  </p>
                )}
              </div>

              <div className="p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 mb-2">
                  <Server className="h-4 w-4 text-primary" />
                  <p className="text-sm font-semibold">Zone Status</p>
                </div>
                {operations.zone ? (
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>Status: <span className="text-foreground">{operations.zone.status}</span></p>
                    <p>Plan: <span className="text-foreground">{operations.zone.plan}</span></p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Not found in Cloudflare
                  </p>
                )}
              </div>

              <div className="p-4 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 mb-2">
                  <Link2 className="h-4 w-4 text-primary" />
                  <p className="text-sm font-semibold">Pages Project</p>
                </div>
                {operations.pages ? (
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>
                      {operations.pages.hasProject ? 'Connected' : 'Not found'}
                    </p>
                    {operations.pages.projectName && (
                      <p className="text-xs">{operations.pages.projectName}</p>
                    )}
                    {operations.pages.subdomain && (
                      <p className="text-xs text-foreground">
                        {operations.pages.subdomain}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No Pages data
                  </p>
                )}
              </div>
            </div>

            {operations.zone?.nameservers?.length ? (
              <div>
                <p className="text-sm font-semibold mb-2">Nameservers</p>
                <div className="flex flex-wrap gap-2">
                  {operations.zone.nameservers.map((ns) => (
                    <span key={ns} className="badge badge-muted text-xs">
                      {ns}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            {operations.dns ? (
              <div>
                <p className="text-sm font-semibold mb-2">DNS Records</p>
                <div className="flex flex-wrap gap-2">
                  <span className="badge badge-muted text-xs">
                    Total: {operations.dns.total}
                  </span>
                  {Object.entries(operations.dns.byType).map(([type, count]) => (
                    <span key={type} className="badge badge-muted text-xs">
                      {type}: {count}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            {operations.recommendations?.length ? (
              <div className="p-4 rounded-lg border border-border bg-muted/30">
                <p className="text-sm font-semibold mb-2">Recommended Actions</p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {operations.recommendations.map((item, index) => (
                    <li key={index}>• {item}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Generate CTA */}
      {analysis && (
        <div className="card bg-gradient-to-r from-primary/10 via-secondary/10 to-accent/10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold mb-1">Ready to generate your site?</h3>
              <p className="text-sm text-muted-foreground">
                Use AI analysis to create a professional site in minutes
              </p>
            </div>
            <Link
              to={`/dashboard/generate/${domain.id}`}
              className="btn-primary"
            >
              <Zap className="h-4 w-4" />
              Generate Site
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
